import { match } from "ts-pattern";
import type { SnakeCase } from "type-fest";
import type { ZodType } from "zod";
import z from "zod";

import { looseUuid } from "@thalamos/common";

import {
  ConfigurationScopeIdSchema,
  type ConfigurationScopeIdUnbranded,
} from "./configuration/scope.js";

export type OrganisationIdUnbranded = string;
export const OrganisationIdSchema =
  looseUuid().brand<"haven-common:OrganisationId">() satisfies ZodType<OrganisationIdUnbranded>;
/**
 * A body that acts as a data controller — a local authority, a trust, a
 * placement provider.
 */
export type OrganisationId = z.infer<typeof OrganisationIdSchema>;

export type OrganisationRelationshipIdUnbranded = string;
export const OrganisationRelationshipIdSchema =
  looseUuid().brand<"haven-common:OrganisationRelationshipId">() satisfies ZodType<OrganisationRelationshipIdUnbranded>;
/**
 * One stored link between two organisations, such as one being a member of the
 * other (`docs/glossary/organisation-relationship.md`). Branded apart from
 * `OrganisationId` because a link and the two organisations it joins are all
 * UUIDs, held side by side in one record.
 */
export type OrganisationRelationshipId = z.infer<
  typeof OrganisationRelationshipIdSchema
>;

/**
 * A body someone holds a membership at.
 *
 * Carries what identifies it and nothing about how it stands to another
 * organisation: those links are relationships of their own, read separately,
 * because a field naming one other body could not say which way the link runs or
 * what it carries.
 */
export type OrganisationUnbranded = {
  id: OrganisationIdUnbranded;
  name: string;
  type: OrganisationType;
};

/**
 * What kind of body an organisation is.
 *
 * Closed in the application schema rather than in the column, for the reason
 * `PersonInvolvementKind` gives.
 *
 * `unknown` is a real member and not an absence: it is what every organisation
 * recorded before types existed is, and it says nobody has yet decided. A body
 * of unknown kind may hold no involvement, and no picker offers one, so it is
 * visibly unusable rather than quietly miscategorised.
 */
export type OrganisationType =
  | "local_authority"
  | "provider"
  | "residential_unit"
  | "education"
  | "health"
  | "oversight"
  | "unknown";

export const OrganisationTypeSchema = z.enum([
  "local_authority",
  "provider",
  "residential_unit",
  "education",
  "health",
  "oversight",
  "unknown",
]) satisfies ZodType<OrganisationType & SnakeCase<OrganisationType>>;

/**
 * How one organisation stands to another, read from the first one's side.
 * `member_of` carries what a member holds for a young person up to the
 * organisation above it; `operated_by` records who runs a body and carries
 * nothing.
 */
export type OrganisationRelationshipKind = "member_of" | "operated_by";

export const OrganisationRelationshipKindSchema = z.enum([
  "member_of",
  "operated_by",
]) satisfies ZodType<
  OrganisationRelationshipKind & SnakeCase<OrganisationRelationshipKind>
>;

/** The kinds a picker offers, which is every kind anybody has decided on. */
export const CHOOSABLE_ORGANISATION_TYPES =
  OrganisationTypeSchema.options.filter((type) => type !== "unknown");

/**
 * The kinds a request may ask a listing for.
 *
 * At least one, and never `unknown`. A body nobody has stated the kind of may be
 * given no part in a child's care, so offering one in a picker would invite a
 * write the server then refuses; and a listing with no kind at all is the
 * enumeration of every body in Haven, which is the administrative listing's job
 * rather than a customer picker's.
 */
export const RequestedOrganisationTypesSchema = z
  .array(OrganisationTypeSchema.exclude(["unknown"]))
  .min(1, "organisation.type.required");

/**
 * Branded, so holding an `Organisation` means one was parsed rather than
 * assembled from a row that merely looked right.
 */
export const OrganisationSchema = z
  .object({
    id: OrganisationIdSchema,
    name: z.string().min(1).max(255),
    type: OrganisationTypeSchema,
  })
  .brand<"haven-common:Organisation">() satisfies ZodType<OrganisationUnbranded>;

export type Organisation = z.infer<typeof OrganisationSchema>;

/**
 * Recording what kind of body an organisation is. Only the kind: renaming is
 * `RenameOrganisationRequestUnbranded`.
 */
export type SetOrganisationTypeRequestUnbranded = {
  type: OrganisationType;
};

export const SetOrganisationTypeRequestSchema = z
  .object({
    type: OrganisationTypeSchema,
  })
  .brand<"haven-common:SetOrganisationTypeRequest">() satisfies ZodType<SetOrganisationTypeRequestUnbranded>;

export type SetOrganisationTypeRequest = z.infer<
  typeof SetOrganisationTypeRequestSchema
>;

/** The longest a name may be, which is the width of the `organisations.name` column. */
const ORGANISATION_NAME_MAX_LENGTH = 255;

/**
 * The kinds the administrative listing may be narrowed to.
 *
 * `unknown` included, unlike `RequestedOrganisationTypesSchema`: finding the
 * bodies nobody has stated the kind of is how they come to be given one.
 */
export const AdministeredOrganisationTypesSchema = z.array(
  OrganisationTypeSchema,
);

/**
 * Creating an organisation: its name, its kind and the configuration it is set
 * to.
 *
 * The kind is one somebody has decided on, so `unknown` is refused: it describes
 * the bodies recorded before kinds existed and is never a choice. The
 * configuration is required so that no organisation is created without one.
 */
export type CreateOrganisationRequestUnbranded = {
  name: string;
  type: OrganisationType;
  configurationScopeId: ConfigurationScopeIdUnbranded;
};

const OrganisationNameSchema = z
  .string()
  .trim()
  .min(1, "organisation.name.required")
  .max(ORGANISATION_NAME_MAX_LENGTH, "organisation.name.tooLong");

export const CreateOrganisationRequestSchema = z
  .object({
    name: OrganisationNameSchema,
    type: OrganisationTypeSchema.exclude(["unknown"], {
      error: "organisation.kind.required",
    }),
    configurationScopeId: ConfigurationScopeIdSchema,
  })
  .brand<"haven-common:CreateOrganisationRequest">() satisfies ZodType<CreateOrganisationRequestUnbranded>;

export type CreateOrganisationRequest = z.infer<
  typeof CreateOrganisationRequestSchema
>;

/** Renaming an organisation. */
export type RenameOrganisationRequestUnbranded = {
  name: string;
};

export const RenameOrganisationRequestSchema = z
  .object({ name: OrganisationNameSchema })
  .brand<"haven-common:RenameOrganisationRequest">() satisfies ZodType<RenameOrganisationRequestUnbranded>;

export type RenameOrganisationRequest = z.infer<
  typeof RenameOrganisationRequestSchema
>;

/** The level of the configuration chain an organisation is attached to. */
export type OrganisationConfigurationLevelUnbranded = {
  id: ConfigurationScopeIdUnbranded;
  name: string;
};

/**
 * An organisation as Thalamos administers it: what identifies it, the level of
 * the configuration chain it takes its settings from (`null` where it is
 * attached to none), and whether it reads what its members' finished
 * assessments say.
 */
export type AdministeredOrganisationUnbranded = OrganisationUnbranded & {
  configurationScope: OrganisationConfigurationLevelUnbranded | null;
  readsApprovedAssessmentsThroughMembers: boolean;
};

export const AdministeredOrganisationSchema = z.object({
  id: OrganisationIdSchema,
  name: z.string().min(1).max(ORGANISATION_NAME_MAX_LENGTH),
  type: OrganisationTypeSchema,
  configurationScope: z
    .object({ id: ConfigurationScopeIdSchema, name: z.string() })
    .nullable(),
  readsApprovedAssessmentsThroughMembers: z.boolean(),
}) satisfies ZodType<AdministeredOrganisationUnbranded>;

export type AdministeredOrganisation = z.infer<
  typeof AdministeredOrganisationSchema
>;

/**
 * Whether an organisation reads what a finished assessment says about a young
 * person it reaches only through one of its members.
 */
export type SetReadsApprovedAssessmentsThroughMembersRequestUnbranded = {
  value: boolean;
};

export const SetReadsApprovedAssessmentsThroughMembersRequestSchema = z
  .object({ value: z.boolean() })
  .brand<"haven-common:SetReadsApprovedAssessmentsThroughMembersRequest">() satisfies ZodType<SetReadsApprovedAssessmentsThroughMembersRequestUnbranded>;

export type SetReadsApprovedAssessmentsThroughMembersRequest = z.infer<
  typeof SetReadsApprovedAssessmentsThroughMembersRequestSchema
>;

/**
 * How another organisation stands to this one, worded from this one's side.
 *
 * A relationship is stored once, read from the first organisation's side, so each
 * stored kind has two wordings: `member_of` is `has_member` from the other end,
 * and `operated_by` is `operates`.
 */
export type OrganisationRelation =
  "member_of" | "has_member" | "operated_by" | "operates";

export const OrganisationRelationSchema = z.enum([
  "member_of",
  "has_member",
  "operated_by",
  "operates",
]) satisfies ZodType<OrganisationRelation & SnakeCase<OrganisationRelation>>;

/**
 * Where a relationship worded from one organisation's side is stored.
 * `has_member` and `operates` are the other organisation's `member_of` and
 * `operated_by`, so they are stored with the two organisations swapped.
 */
export const storedRelationship = (
  organisationId: OrganisationId,
  relation: OrganisationRelation,
  otherOrganisationId: OrganisationId,
): {
  kind: OrganisationRelationshipKind;
  organisationId: OrganisationId;
  relatedOrganisationId: OrganisationId;
} =>
  match(relation)
    .with("member_of", "operated_by", (kind) => ({
      kind,
      organisationId,
      relatedOrganisationId: otherOrganisationId,
    }))
    .with("has_member", () => ({
      kind: "member_of" as const,
      organisationId: otherOrganisationId,
      relatedOrganisationId: organisationId,
    }))
    .with("operates", () => ({
      kind: "operated_by" as const,
      organisationId: otherOrganisationId,
      relatedOrganisationId: organisationId,
    }))
    .exhaustive();

/** Relating this organisation to another, worded from this one's side. */
export type RelateOrganisationRequestUnbranded = {
  relation: OrganisationRelation;
  organisationId: OrganisationIdUnbranded;
};

export const RelateOrganisationRequestSchema = z
  .object({
    relation: OrganisationRelationSchema,
    organisationId: OrganisationIdSchema,
  })
  .brand<"haven-common:RelateOrganisationRequest">() satisfies ZodType<RelateOrganisationRequestUnbranded>;

export type RelateOrganisationRequest = z.infer<
  typeof RelateOrganisationRequestSchema
>;

/** One relationship, and the organisation at its other end. */
export type OrganisationRelationshipUnbranded = {
  id: OrganisationRelationshipIdUnbranded;
  relation: OrganisationRelation;
  organisation: OrganisationUnbranded;
};

/** Wrapped so the response can gain a field without breaking every client's parse. */
export type OrganisationRelationshipsUnbranded = {
  relationships: OrganisationRelationshipUnbranded[];
};

export const OrganisationRelationshipsSchema = z.object({
  relationships: z.array(
    z.object({
      id: OrganisationRelationshipIdSchema,
      relation: OrganisationRelationSchema,
      organisation: OrganisationSchema,
    }),
  ),
}) satisfies ZodType<OrganisationRelationshipsUnbranded>;

export type OrganisationRelationships = z.infer<
  typeof OrganisationRelationshipsSchema
>;

/**
 * A setting an organisation states for itself that decides whether it receives
 * something its members carry up. Written out as a union, as `PersonInvolvementKind`
 * is, so tsoa can follow it into the API contract.
 */
export type OrganisationMembershipGate =
  "readsApprovedAssessmentsThroughMembers";

export const OrganisationMembershipGateSchema = z.enum([
  "readsApprovedAssessmentsThroughMembers",
]) satisfies ZodType<OrganisationMembershipGate>;

/**
 * The most organisations one structure answers with. Past it the answer is cut
 * short and says so, so one request stays quick however large the body.
 */
export const ORGANISATION_STRUCTURE_LIMIT = 1000;

/**
 * The most levels of membership out from an organisation, each way, that one
 * structure reads. Each level is a round trip, so past it the answer is cut
 * short and says so.
 * Real structures are a few levels deep; this leaves room to spare.
 */
export const ORGANISATION_STRUCTURE_DEPTH = 16;

/**
 * The organisations around one, and every relationship among them, as stored:
 * a graph to draw, with no layout. Which organisations count as around it is
 * the server's to decide, so that can widen without any reader changing.
 */
export type OrganisationStructureUnbranded = {
  organisationId: OrganisationIdUnbranded;
  organisations: (OrganisationUnbranded & {
    /** Each setting that decides whether it receives what its members carry up. */
    settings: Record<OrganisationMembershipGate, boolean>;
  })[];
  relationships: {
    id: OrganisationRelationshipIdUnbranded;
    kind: OrganisationRelationshipKind;
    organisationId: OrganisationIdUnbranded;
    relatedOrganisationId: OrganisationIdUnbranded;
  }[];
  /** Whether organisations were left out to keep within `ORGANISATION_STRUCTURE_LIMIT` or `ORGANISATION_STRUCTURE_DEPTH`. */
  truncated: boolean;
};

export const OrganisationStructureSchema = z.object({
  organisationId: OrganisationIdSchema,
  organisations: z.array(
    z.object({
      id: OrganisationIdSchema,
      name: z.string().min(1).max(255),
      type: OrganisationTypeSchema,
      settings: z.record(OrganisationMembershipGateSchema, z.boolean()),
    }),
  ),
  relationships: z.array(
    z.object({
      id: OrganisationRelationshipIdSchema,
      kind: OrganisationRelationshipKindSchema,
      organisationId: OrganisationIdSchema,
      relatedOrganisationId: OrganisationIdSchema,
    }),
  ),
  truncated: z.boolean(),
}) satisfies ZodType<OrganisationStructureUnbranded>;

export type OrganisationStructure = z.infer<typeof OrganisationStructureSchema>;
